import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import Decimal from 'decimal.js';
import { relationshipModel } from '~/models';
import { formatLongMonthDateWithLocalTimezone } from '~/utils';
import { BillingGroupTypes } from '../genServicesSummary';
const BILLING_GROUP = [
    BillingGroupTypes.Today,
    BillingGroupTypes.OnEffectiveDate,
    BillingGroupTypes.InStages,
    BillingGroupTypes.Once,
    BillingGroupTypes.Weekly,
    BillingGroupTypes.BiWeekly,
    BillingGroupTypes.Monthly,
    BillingGroupTypes.Quarterly,
    BillingGroupTypes.Yearly
];
function processServicesInGroup(services, serviceNameToIdMap, servicesInGroupBillingDetails, START_BILLING_AT, now, agreementEffectiveDate) {
    const servicesByStartDate = new Map();
    const servicesWithoutStartDate = [];
    const servicesOnAgreementDate = [];
    const pausedServices = [];
    services.forEach((service)=>{
        var _servicesInGroupBillingDetails_serviceId;
        // paused services go into their own bucket
        if (service.isPaused) {
            pausedServices.push(service);
            return;
        }
        const serviceId = serviceNameToIdMap[service.name] || '';
        const priorPeriods = ((_servicesInGroupBillingDetails_serviceId = servicesInGroupBillingDetails[serviceId]) === null || _servicesInGroupBillingDetails_serviceId === void 0 ? void 0 : _servicesInGroupBillingDetails_serviceId.periods) || 0;
        const effectiveStartDate = service.effectiveStartDate;
        // only create separate groups for future dates with no prior periods
        if (effectiveStartDate && priorPeriods === 0 && START_BILLING_AT && effectiveStartDate > now) {
            // check if this date matches the agreement effective date
            const isAgreementDate = agreementEffectiveDate && effectiveStartDate.getTime() === agreementEffectiveDate.getTime();
            if (isAgreementDate) {
                servicesOnAgreementDate.push(service);
            } else {
                // regular dated services grouped by their date
                const startDateKey = effectiveStartDate.getTime().toString();
                if (!servicesByStartDate.has(startDateKey)) {
                    servicesByStartDate.set(startDateKey, []);
                }
                servicesByStartDate.get(startDateKey).push(service);
            }
        } else {
            servicesWithoutStartDate.push(service);
        }
    });
    return {
        servicesByStartDate,
        servicesWithoutStartDate,
        servicesOnAgreementDate,
        pausedServices
    };
}
function createBillingGroup(param) {
    let { group, groupType, variablePrice, services, serviceNameToIdMap, serviceBillingDetails, START_BILLING_AT, startDate, isPaused } = param;
    const totalAmount = services.reduce((sum, service)=>{
        var _serviceBillingDetails_serviceId;
        let serviceAmount = service.price;
        const serviceId = serviceNameToIdMap[service.name] || '';
        const priorPeriods = ((_serviceBillingDetails_serviceId = serviceBillingDetails[serviceId]) === null || _serviceBillingDetails_serviceId === void 0 ? void 0 : _serviceBillingDetails_serviceId.periods) || 0;
        if (groupType === BillingGroupTypes.Today && priorPeriods > 0 && START_BILLING_AT) {
            serviceAmount = service.price.times(new Decimal(priorPeriods));
        }
        return sum.plus(serviceAmount);
    }, new Decimal(0));
    return {
        group: _object_spread_props(_object_spread({}, group), {
            services,
            totalAmount,
            startDate
        }),
        groupType,
        variablePrice,
        startDate,
        isPaused
    };
}
export function getBillingGroupTitle(groupType, startDate, relationship, isPaused) {
    // paused services get "upon activation" suffix
    if (isPaused) {
        return "Billed ".concat(groupType, " upon activation");
    }
    // OnEffectiveDate with a date = "Billed on [date]" (no period indication)
    if (groupType === BillingGroupTypes.OnEffectiveDate && startDate) {
        return "Billed on ".concat(formatLongMonthDateWithLocalTimezone(startDate));
    }
    if (groupType === BillingGroupTypes.OnEffectiveDate && relationship) {
        return "Billed ".concat(groupType, " (").concat(relationshipModel.formatEffectiveDate(relationship), ")");
    }
    if (startDate) {
        return "Billed ".concat(groupType, " starting on ").concat(formatLongMonthDateWithLocalTimezone(startDate));
    }
    return "Billed ".concat(groupType);
}
export function processBillingGroups(billingSummary, serviceNameToIdMap, serviceBillingDetails, START_BILLING_AT, now, agreementEffectiveDate) {
    const groups = [];
    const allServicesOnAgreementDate = [];
    BILLING_GROUP.forEach((groupType)=>{
        [
            false,
            true
        ].forEach((variablePrice)=>{
            var _billingSummary_groupType;
            const group = (_billingSummary_groupType = billingSummary[groupType]) === null || _billingSummary_groupType === void 0 ? void 0 : _billingSummary_groupType.get(variablePrice);
            if (!group) return;
            const { servicesByStartDate, servicesWithoutStartDate, servicesOnAgreementDate, pausedServices } = processServicesInGroup(group.services, serviceNameToIdMap, serviceBillingDetails, START_BILLING_AT, now, agreementEffectiveDate);
            // collect agreement date services from all period groups (including InStages)
            allServicesOnAgreementDate.push(...servicesOnAgreementDate);
            // create regular group for services without start date
            // for InStages, this will be services whose first stage is NOT on agreement date
            if (servicesWithoutStartDate.length > 0) {
                groups.push(createBillingGroup({
                    group,
                    groupType,
                    variablePrice,
                    services: servicesWithoutStartDate,
                    serviceNameToIdMap,
                    serviceBillingDetails,
                    START_BILLING_AT
                }));
            }
            // create period-specific groups for each start date
            const sortedStartDateEntries = Array.from(servicesByStartDate.entries()).sort((param, param1)=>{
                let [startDateKeyA] = param, [startDateKeyB] = param1;
                return parseInt(startDateKeyA) - parseInt(startDateKeyB);
            });
            sortedStartDateEntries.forEach((param)=>{
                let [startDateKey, services] = param;
                const startDate = new Date(parseInt(startDateKey));
                groups.push(createBillingGroup({
                    group,
                    groupType,
                    variablePrice,
                    services,
                    serviceNameToIdMap,
                    serviceBillingDetails,
                    START_BILLING_AT,
                    startDate
                }));
            });
            // create paused group for paused services
            if (pausedServices.length > 0) {
                groups.push(createBillingGroup({
                    group,
                    groupType,
                    variablePrice,
                    services: pausedServices,
                    serviceNameToIdMap,
                    serviceBillingDetails,
                    START_BILLING_AT,
                    isPaused: true
                }));
            }
        });
    });
    // create single OnEffectiveDate group for all services billed on agreement date
    if (allServicesOnAgreementDate.length > 0 && agreementEffectiveDate) {
        const totalAmount = allServicesOnAgreementDate.reduce((sum, s)=>sum.plus(s.price), new Decimal(0));
        const todayIndex = groups.findIndex((g)=>g.groupType === BillingGroupTypes.Today);
        groups.splice(todayIndex + 1, 0, {
            groupType: BillingGroupTypes.OnEffectiveDate,
            variablePrice: false,
            startDate: agreementEffectiveDate,
            group: {
                totalAmount,
                services: allServicesOnAgreementDate,
                hasBilledUpfront: true
            }
        });
    }
    return groups;
}
